/**
 * SceneEditorHeader Component — V2 Simplified
 * Feature: Scene Editor Redesign
 *
 * Single-row header: Left (nav + badges), Center (frame toggle + regen),
 * Right (Generate button).
 */

import {
  ArrowLeft,
  Layers,
  Play,
  Frame,
  Columns2,
  RefreshCw,
  Check,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import type { ISceneEditorHeaderV2Props } from '@/@types/sceneEditorRedesign';

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function SceneEditorHeader({
  sceneCount,
  totalDuration,
  onBack,
  frameMode,
  onToggleFrameMode,
  onOpenRegenerateMedia,
  onPlayPreview,
  onGenerate,
  isGenerating,
  selectedSceneType,
  onRegenerateVideo,
  generateLabel,
  generateProgress,
  onViewFinalVideo,
}: ISceneEditorHeaderV2Props) {
  return (
    <div className="flex h-14 items-center border-b border-border bg-background-secondary px-6">
      {/* Left group */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-foreground-muted hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Reels
        </button>
        {sceneCount > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium text-primary border-primary/25 bg-[var(--scene-number-bg)] light:text-primary-foreground light:border-primary-foreground/25">
            <Layers className="h-3 w-3" />
            {sceneCount}
          </span>
        )}
        {totalDuration > 0 && (
          <span className="text-xs text-foreground-muted">{formatDuration(totalDuration)}</span>
        )}
        <button
          onClick={onPlayPreview}
          className="ml-1 flex h-7 w-7 items-center justify-center rounded-full border border-border hover:bg-background transition-colors"
        >
          <Play className="h-3 w-3" />
        </button>
      </div>

      {/* Center group */}
      <div className="flex flex-1 items-center justify-center gap-2">
        {selectedSceneType === 'avatar_only' ? (
          <button
            onClick={onRegenerateVideo}
            className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs hover:bg-background transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Regenerate Video
          </button>
        ) : (
          <>
            <button
              onClick={onToggleFrameMode}
              className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs hover:bg-background transition-colors"
            >
              {frameMode === 'start-only' ? (
                <Columns2 className="h-3.5 w-3.5" />
              ) : (
                <Frame className="h-3.5 w-3.5" />
              )}
              {frameMode === 'start-only' ? 'Use start-end frames' : 'Use start frame only'}
            </button>
            <button
              onClick={onOpenRegenerateMedia}
              className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs hover:bg-background transition-colors"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Regenerate Media
            </button>
          </>
        )}
      </div>

      {/* Right group */}
      <button
        onClick={generateLabel === 'Done!' && onViewFinalVideo ? onViewFinalVideo : onGenerate}
        disabled={isGenerating}
        className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
      >
        {isGenerating ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {generateLabel ?? 'Generating...'}
            {generateProgress != null && generateProgress > 0 && (
              <span className="ml-1 text-xs opacity-80">{generateProgress}%</span>
            )}
          </>
        ) : generateLabel === 'Done!' ? (
          <>
            <Play className="h-3.5 w-3.5" />
            View Final Video
          </>
        ) : generateLabel?.startsWith('Failed') ? (
          <>
            <AlertCircle className="h-3.5 w-3.5" />
            Retry
          </>
        ) : (
          <>
            <Check className="h-3.5 w-3.5" />
            Generate
          </>
        )}
      </button>
    </div>
  );
}
